//client/src/components/plataforma/siteInstitucional/PaginasListPage.js
import React, { Fragment, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePages } from '../../../hooks/usePages';
import useAlert from '../../../hooks/useAlert'; // ✅ Importar o hook de alerta
import { Plus, Edit, Trash2, Loader2, FileText, MoreVertical } from 'lucide-react';
import PageHeader from '../../layout/PageHeader';
import FormModal from '../../layout/FormModal'; // ✅ Importar o modal
import FabButton from '../../common/FabButton';
import ActionsDrawer from '../../common/ActionsDrawer';

const PaginasListPage = () => {
    const navigate = useNavigate();
    const { pages, loading, error, deletePage, refetch } = usePages();
    const { setAlert } = useAlert(); // ✅ Usar o hook de alerta

    // ✅ Estado para controlar o modal de exclusão
    const [deleteModal, setDeleteModal] = useState({ isOpen: false, pageId: null, pageTitle: '' });
    const [selectedPage, setSelectedPage] = useState(null);

    const openDeleteModal = (id, title) => {
        setDeleteModal({ isOpen: true, pageId: id, pageTitle: title });
        setSelectedPage(null);
    };

    const handleEditPage = (page) => {
        setSelectedPage(null);
        navigate(`/site-institucional/paginas/editar/${page._id}`);
    };

    const closeDeleteModal = () => {
        setDeleteModal({ isOpen: false, pageId: null, pageTitle: '' });
    };

    const confirmDelete = async () => {
        if (!deleteModal.pageId) return;
        try {
            await deletePage(deleteModal.pageId);
            setAlert('Página excluída com sucesso!', 'success');
            refetch(); // Recarrega a lista de páginas
        } catch (err) {
            setAlert(`Erro ao excluir página: ${err.message}`, 'error');
        } finally {
            closeDeleteModal();
        }
    };

    if (error) {
        // O componente Alert global já deve exibir o erro, mas podemos ter um fallback
        return <div className="page-management-container"><div className="alert alert-danger">Erro ao carregar páginas: {error}</div></div>;
    }

    return (
        <Fragment>
            <div className="page-management-container">
                <PageHeader
                    title="Páginas do Site"
                    icon={<FileText className="icon-title" />}
                />
            </div>

            {/* ✅ ACHADO: esta tabela era montada com <table> cru + classes
                Tailwind soltas (min-w-full bg-white rounded-lg shadow), em
                vez do padrão table-container/table-wrapper/styled-table
                documentado como obrigatório no CLAUDE.md — a mesma estrutura
                que toda outra pagina de lista do app usa (Paginas era a
                unica exceção neste diretório: MensagensContatoPage.js, ao
                lado, já usava o padrão certo). */}
            <div className="table-container">
                <div className="table-wrapper">
                    {loading ? (
                        <div className="table-loading-state">
                            <Loader2 size={24} className="animate-spin" />
                            <span>Carregando páginas...</span>
                        </div>
                    ) : pages.length > 0 ? (
                        <table className="styled-table">
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'left' }}>Título</th>
                                    <th style={{ textAlign: 'left' }}>Slug (URL)</th>
                                    <th>Status</th>
                                    <th className="text-right">Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pages.map(page => (
                                    <tr key={page._id}>
                                        <td style={{ textAlign: 'left' }}>{page.title}</td>
                                        <td style={{ textAlign: 'left', color: 'var(--color-text-secondary)' }}>/{page.slug}</td>
                                        <td>
                                            <span className={`badge ${page.status === 'published' ? 'badge-success' : 'badge-warning'}`}>
                                                {page.status === 'published' ? 'Publicado' : 'Rascunho'}
                                            </span>
                                        </td>
                                        <td className="text-right">
                                            <button onClick={() => setSelectedPage(page)} className="action-icon-button text-gray-600" title="Ações">
                                                <MoreVertical size={18} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <div className="table-empty-state">
                            <FileText size={48} />
                            <h3>Nenhuma página encontrada</h3>
                        </div>
                    )}
                </div>
            </div>

            {/* ✅ Modal de confirmação de exclusão */}
            {deleteModal.isOpen && (
                <FormModal title="Confirmar Exclusão" onClose={closeDeleteModal} size="small">
                    <p>Você tem certeza que deseja excluir a página "<strong>{deleteModal.pageTitle}</strong>"?</p>
                    <p className="text-sm text-gray-600 mt-2">Esta ação não pode ser desfeita.</p>
                    <div className="form-actions">
                        <button type="button" onClick={closeDeleteModal} className="action-button action-button-secondary">Cancelar</button>
                        <button type="button" onClick={confirmDelete} className="action-button action-button-danger">Confirmar Exclusão</button>
                    </div>
                </FormModal>
            )}

            <ActionsDrawer
                open={!!selectedPage}
                onClose={() => setSelectedPage(null)}
                title={selectedPage?.title || 'Página'}
                actions={selectedPage ? [
                    {
                        key: 'editar',
                        label: 'Editar',
                        icon: <Edit size={20} className="icon" />,
                        onClick: () => handleEditPage(selectedPage),
                    },
                    {
                        key: 'excluir',
                        label: 'Excluir',
                        icon: <Trash2 size={20} className="icon" />,
                        onClick: () => openDeleteModal(selectedPage._id, selectedPage.title),
                        destructive: true,
                    },
                ] : []}
            />

            <FabButton onClick={() => navigate('/site-institucional/paginas/nova')} title="Nova Página" icon={<FileText size={26} className="icon" />} />
        </Fragment>
    );
};

export default PaginasListPage;